import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Loader2, CheckCircle2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import SquareButton from '@/components/SquareButton';
import { postJson } from '@/lib/api';

const inputCls = 'mt-2 h-12 border-2 border-[hsl(var(--c-ink))] bg-white px-3 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:border-[hsl(var(--c-mango-orange))]';
const labelCls = 'eyebrow text-[hsl(var(--c-ink))]/70';

export default function ContactForm({ defaultSubject = '', sourcePage = 'contact' }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: defaultSubject, message: '', honeypot: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (defaultSubject) setForm((s) => ({ ...s, subject: defaultSubject }));
  }, [defaultSubject]);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await postJson('/contact', { ...form, source_page: sourcePage });
      setSubmitted(true);
      toast.success(res.message || 'Message sent!');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="py-12 text-center" data-testid="contact-success">
        <div className="mx-auto inline-flex h-16 w-16 items-center justify-center bg-[hsl(var(--c-leaf))] border-2 border-[hsl(var(--c-ink))]">
          <CheckCircle2 className="h-8 w-8 text-white" />
        </div>
        <h3 className="mt-6 font-heading text-3xl sm:text-4xl font-extrabold">Message received</h3>
        <p className="mt-3 text-[hsl(var(--c-ink-soft))] max-w-lg mx-auto">
          Thanks for reaching out — we’ll be in touch soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-5" data-testid="contact-inquiry-form">
      <input type="text" name="website" value={form.honeypot} onChange={(e) => update('honeypot', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="c-name" className={labelCls}>Full Name *</Label>
          <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => update('name', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="c-email" className={labelCls}>Email *</Label>
          <Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={(e) => update('email', e.target.value)} className={inputCls} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="c-phone" className={labelCls}>Phone</Label>
          <Input id="c-phone" data-testid="contact-phone-input" value={form.phone} onChange={(e) => update('phone', e.target.value)} className={inputCls} />
        </div>
        <div>
          <Label htmlFor="c-subject" className={labelCls}>Subject</Label>
          <Input id="c-subject" data-testid="contact-subject-input" value={form.subject} onChange={(e) => update('subject', e.target.value)} className={inputCls} placeholder="How can we help?" />
        </div>
      </div>

      <div>
        <Label htmlFor="c-message" className={labelCls}>Message *</Label>
        <Textarea id="c-message" data-testid="contact-message-input" required value={form.message} onChange={(e) => update('message', e.target.value)} className={`${inputCls} h-auto py-3`} rows={5} />
      </div>

      <SquareButton type="submit" disabled={submitting} variant="orange" size="lg" fullWidth testId="contact-form-submit-button">
        {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>) : 'Send Message'}
      </SquareButton>
    </form>
  );
}
